import { AfterViewInit, Component, ElementRef, OnInit, ViewChild, input, output, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  QaiAccordianPanelComponent,
  QaiFloatingDirective,
  QaiIconComponent,
  QaiLabelComponent,
  QaiTooltipComponent,
} from '@qai/ui';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { AuthorReactiveFormService } from 'src/app/pages/self-serve/simulation-builder/services/author-reactive-form.service';
import { isComposingKeyEvent } from 'src/app/utils/keyboard-event.util';
import { KnowledgeCheckQuestionFieldsComponent } from './knowledge-check-question-fields/knowledge-check-question-fields.component';
import { KnowledgeCheckAnswerPointsComponent } from './knowledge-check-answer-points/knowledge-check-answer-points.component';

// One Knowledge Check question as an accordion panel: title (renamed inline), answer point
// count, and the question fields. The drag handle is active once the question is saved (see [draggable]).
@Component({
  selector: 'knowledge-check-question-panel',
  standalone: true,
  templateUrl: './knowledge-check-question-panel.component.html',
  styleUrls: ['./knowledge-check-question-panel.component.scss'],
  imports: [
    ReactiveFormsModule,
    QaiAccordianPanelComponent,
    QaiIconComponent,
    QaiLabelComponent,
    QaiTooltipComponent,
    QaiFloatingDirective,
    KnowledgeCheckQuestionFieldsComponent,
    KnowledgeCheckAnswerPointsComponent,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class KnowledgeCheckQuestionPanelComponent implements OnInit, AfterViewInit {
  questionForm = input.required<FormGroup>();
  draggable = input(false);
  // True while this question's delete request is in flight.
  deleting = input(false);
  initialExpanded = input(false);
  shouldStartEditing = input(false);
  answerPointListId = input<string>();
  connectedAnswerPointListIds = input<string[]>([]);
  deleteRequested = output<void>();
  // The answer points before a drop reordered them (or moved one in).
  answerPointsReordered = output<FormGroup[]>();
  // Drives the accordion: it follows [initialExpanded], so setting this expands it from code.
  isExpanded = signal(false);
  // The accordion's drag handle, which anchors the "save before reordering" tooltip.
  dragHandle = signal<HTMLElement | undefined>(undefined);

  @ViewChild('titleInput') titleInputRef?: ElementRef<HTMLInputElement>;

  callUuid: string = '';
  readOnly: boolean = false;
  isEditingTitle: boolean = false;
  titleControl = new FormControl('');

  constructor(
    public elementRef: ElementRef<HTMLElement>,
    private route: ActivatedRoute,
    private authorReactiveFormService: AuthorReactiveFormService,
  ) {}

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap?.get('uuid')!;
    this.readOnly = this.authorReactiveFormService.isReadOnly(this.callUuid);
    this.isExpanded.set(this.initialExpanded());
    if (this.shouldStartEditing()) this.startEditing();
  }

  // The handle lives inside qai-accordian-panel's template, so it is looked up once the panel has rendered.
  ngAfterViewInit(): void {
    this.dragHandle.set(this.elementRef.nativeElement.querySelector<HTMLElement>('.panel-drag-handle') ?? undefined);
  }

  get questionTitle(): string {
    return (this.questionForm().get('title')?.value ?? '').trim();
  }

  get answerPointsCount(): number {
    return (this.questionForm().get('answerPoints') as FormArray | null)?.length ?? 0;
  }

  expand(): void {
    this.isExpanded.set(true);
  }

  // qai-dragable turns pointer events back on for every drag handle, so the greyed-out handle of an
  // unsaved question would still start a drag. Stopping the press here keeps it from reaching the drag.
  blockDisabledDrag(event: Event): void {
    if (this.draggable() && !this.readOnly) return;
    if ((event.target as Element | null)?.closest('.panel-drag-handle')) event.stopPropagation();
  }

  startEditing(): void {
    this.titleControl.setValue(this.questionTitle);
    this.isEditingTitle = true;
    setTimeout(() => {
      this.titleInputRef?.nativeElement?.focus();
      this.titleInputRef?.nativeElement?.select();
    });
  }

  handleTitleInputKeydown(event: Event): void {
    if (isComposingKeyEvent(event as KeyboardEvent)) return;
    this.stopEditingTitle();
  }

  stopEditingTitle(): void {
    if (!this.isEditingTitle) return;
    this.isEditingTitle = false;

    // An empty title is allowed: the header then shows the "Untitled question" placeholder.
    const newTitle = this.titleControl.value?.trim() ?? '';
    if (newTitle !== this.questionTitle) {
      this.questionForm().patchValue({ title: newTitle });
    }
  }
}
